import {useMemo} from "react";
import {useSelector} from "react-redux";

import {IOption} from "@pg-design/select-module";

import {useGetClientInfoQuery} from "../../client/api/get_client_info";
import {useShouldShowPriceByOfferConfigAndUser} from "../../hooks/use_should_show_price_by_offer_config_and_user";
import {useGetOfferDetailQuery} from "../../offer/api/get_offer_detail";
import {useGetPropertyDetailQuery} from "../../property/api/get_property_detail";
import {IRootState} from "../../store/store";
import {ISurveyData} from "../types/IBuildSurveyParams";
import {IPreferencesDefaultValues} from "../types/IPreferencesDefaultValues";
import {buildSurvey, BuildSurveyResult} from "../utils/survey/build_survey";
import {mapOfferDetailsToSurveyOfferDetails} from "../utils/survey/map_offer_details_to_survey_offer_details";
import {mapPropertyDetailsToSurveyPropertyDetails} from "../utils/survey/map_property_details_to_survey_property_details";
import {mapUserPreferencesToSurveyFiltersData} from "../utils/survey/map_user_preferences_to_survey_filters_data";

const normalizeSurveyDefaults = (surveyResult: BuildSurveyResult, maxBudget: number, regionOptions: IOption<number>[]): IPreferencesDefaultValues => {
    const regions = surveyResult.regions || [];
    const rooms = surveyResult.rooms || {lower: 1, upper: 5};
    const price = surveyResult.price?.upper;

    // Map region IDs to option format
    const regionSelector: IOption<number>[] = regions
        .map((regionId) => {
            const existingOption = regionOptions.find((opt) => opt.value === regionId);

            if (existingOption) {
                return existingOption;
            }

            // If region not found in options, create a placeholder
            return {value: regionId, label: ""};
        })
        .filter(Boolean);

    return {
        regionSelector,
        regions: regionSelector,
        budget: price && price <= maxBudget ? price : maxBudget,
        rooms: {
            lower: rooms.lower || "",
            upper: rooms.upper || ""
        },
        creditPlan: false
    };
};

export interface IUseSurveyDefaultValuesOptions {
    maxBudget: number;
    regionOptions?: IOption<number>[];
}

export interface IUseSurveyDefaultValuesResult {
    defaultValues: IPreferencesDefaultValues;
    surveyResult: BuildSurveyResult;
    hasPropertyContext: boolean;
    hasOfferContext: boolean;
    isLoading: boolean;
    displayedPrice: number | null;
}

/**
 * Hook that calculates default values for survey/preferences form
 * based on property details, offer details, filters, and existing survey data.
 *
 * Scenarios:
 * 1. Only property details (new user): elevate region to district/county, exact rooms, price rounded to 5k
 * 2. Only offer details (new user): elevate region to district/county, rooms from offer range, max price rounded to 5k
 * 3. Filters + property/offer: merge regions, widest rooms range, max price rounded to 5k
 * 4. Survey + property/offer: add region, expand rooms range, raise price only if property/offer is more expensive
 */
export const useSurveyDefaultValues = (options: IUseSurveyDefaultValuesOptions): IUseSurveyDefaultValuesResult => {
    const {maxBudget, regionOptions = []} = options;

    const propertyId = useSelector((state: IRootState) => state.registrationFlowVariantB?.propertyId);
    const offerId = useSelector((state: IRootState) => state.registrationFlowVariantB?.offerId);

    const {data: propertyDetail, isLoading: isPropertyLoading} = useGetPropertyDetailQuery(propertyId!, {skip: !propertyId});
    const {data: offerDetail, isLoading: isOfferLoading} = useGetOfferDetailQuery(offerId!, {skip: !offerId});
    const {data: clientInfo} = useGetClientInfoQuery();

    const userPreferences = useSelector((state: IRootState) => state.user?.preferences?.data);

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: offerDetail?.configuration.price_visibility,
        vendorPriceVisibility: offerDetail?.vendor.configuration.price_visibility,
        vendorId: offerDetail?.vendor.id ?? 0
    });

    const displayedPrice = shouldShowPrice ? (propertyDetail ? propertyDetail.price : (offerDetail?.stats?.ranges_price_min ?? null)) : null;

    const userSurvey: ISurveyData | null = clientInfo ?? null;

    const surveyResult = useMemo(() => {
        const propertyDetailsNormalized = mapPropertyDetailsToSurveyPropertyDetails(propertyDetail);
        let offerDetailsNormalized = mapOfferDetailsToSurveyOfferDetails(offerDetail);
        const filtersNormalized = mapUserPreferencesToSurveyFiltersData(userPreferences);

        // If we have context for property or filters - than do not use offer context. Offer context may lead to the survey value being too broad.
        if (propertyDetailsNormalized || filtersNormalized) {
            offerDetailsNormalized = null;
        }

        return buildSurvey(propertyDetailsNormalized, offerDetailsNormalized, filtersNormalized, userSurvey);
    }, [propertyDetail, offerDetail, userPreferences, userSurvey]);

    const defaultValues = useMemo(() => {
        return normalizeSurveyDefaults(surveyResult, maxBudget, regionOptions);
    }, [surveyResult, maxBudget, regionOptions]);

    const isLoading = isPropertyLoading || isOfferLoading;

    return {
        defaultValues,
        surveyResult,
        hasPropertyContext: !!propertyDetail,
        hasOfferContext: !!offerDetail,
        displayedPrice,
        isLoading
    };
};
import {useMemo} from "react";
import {useSelector} from "react-redux";

import {IOption} from "@pg-design/select-module";

import {useGetClientInfoQuery} from "../../client/api/get_client_info";
import {useShouldShowPriceByOfferConfigAndUser} from "../../hooks/use_should_show_price_by_offer_config_and_user";
import {useGetOfferDetailQuery} from "../../offer/api/get_offer_detail";
import {useGetPropertyDetailQuery} from "../../property/api/get_property_detail";
import {IRootState} from "../../store/store";
import {ISurveyData} from "../types/IBuildSurveyParams";
import {IPreferencesDefaultValues} from "../types/IPreferencesDefaultValues";
import {buildSurvey, BuildSurveyResult} from "../utils/survey/build_survey";
import {mapOfferDetailsToSurveyOfferDetails} from "../utils/survey/map_offer_details_to_survey_offer_details";
import {mapPropertyDetailsToSurveyPropertyDetails} from "../utils/survey/map_property_details_to_survey_property_details";
import {mapUserPreferencesToSurveyFiltersData} from "../utils/survey/map_user_preferences_to_survey_filters_data";

const normalizeSurveyDefaults = (surveyResult: BuildSurveyResult, maxBudget: number, regionOptions: IOption<number>[]): IPreferencesDefaultValues => {
    const regions = surveyResult.regions || [];
    const rooms = surveyResult.rooms || {lower: 1, upper: 5};
    const price = surveyResult.price?.upper;

    // Map region IDs to option format
    const regionSelector: IOption<number>[] = regions
        .map((regionId) => {
            const existingOption = regionOptions.find((opt) => opt.value === regionId);

            if (existingOption) {
                return existingOption;
            }

            // If region not found in options, create a placeholder
            return {value: regionId, label: ""};
        })
        .filter(Boolean);

    return {
        regionSelector,
        regions: regionSelector,
        budget: price && price <= maxBudget ? price : maxBudget,
        rooms: {
            lower: rooms.lower || "",
            upper: rooms.upper || ""
        },
        creditPlan: false
    };
};

export interface IUseSurveyDefaultValuesOptions {
    maxBudget: number;
    regionOptions?: IOption<number>[];
}

export interface IUseSurveyDefaultValuesResult {
    defaultValues: IPreferencesDefaultValues;
    surveyResult: BuildSurveyResult;
    hasPropertyContext: boolean;
    hasOfferContext: boolean;
    isLoading: boolean;
    displayedPrice: number | null;
}

/**
 * Hook that calculates default values for survey/preferences form
 * based on property details, offer details, filters, and existing survey data.
 *
 * Scenarios:
 * 1. Only property details (new user): elevate region to district/county, exact rooms, price rounded to 5k
 * 2. Only offer details (new user): elevate region to district/county, rooms from offer range, max price rounded to 5k
 * 3. Filters + property/offer: merge regions, widest rooms range, max price rounded to 5k
 * 4. Survey + property/offer: add region, expand rooms range, raise price only if property/offer is more expensive
 */
export const useSurveyDefaultValues = (options: IUseSurveyDefaultValuesOptions): IUseSurveyDefaultValuesResult => {
    const {maxBudget, regionOptions = []} = options;

    const propertyId = useSelector((state: IRootState) => state.registrationFlowVariantB?.propertyId);
    const offerId = useSelector((state: IRootState) => state.registrationFlowVariantB?.offerId);

    const {data: propertyDetail, isLoading: isPropertyLoading} = useGetPropertyDetailQuery(propertyId!, {skip: !propertyId});
    const {data: offerDetail, isLoading: isOfferLoading} = useGetOfferDetailQuery(offerId!, {skip: !offerId});
    const {data: clientInfo} = useGetClientInfoQuery();

    const userPreferences = useSelector((state: IRootState) => state.user?.preferences?.data);

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: offerDetail?.configuration.price_visibility,
        vendorPriceVisibility: offerDetail?.vendor.configuration.price_visibility,
        vendorId: offerDetail?.vendor.id ?? 0
    });

    const displayedPrice = shouldShowPrice ? (propertyDetail ? propertyDetail.price : (offerDetail?.stats?.ranges_price_min ?? null)) : null;

    const userSurvey: ISurveyData | null = clientInfo ?? null;

    const surveyResult = useMemo(() => {
        const propertyDetailsNormalized = mapPropertyDetailsToSurveyPropertyDetails(propertyDetail);
        let offerDetailsNormalized = mapOfferDetailsToSurveyOfferDetails(offerDetail);
        const filtersNormalized = mapUserPreferencesToSurveyFiltersData(userPreferences);

        // If we have context for property or filters - than do not use offer context. Offer context may lead to the survey value being too broad.
        if (propertyDetailsNormalized || filtersNormalized) {
            offerDetailsNormalized = null;
        }

        return buildSurvey(propertyDetailsNormalized, offerDetailsNormalized, filtersNormalized, userSurvey);
    }, [propertyDetail, offerDetail, userPreferences, userSurvey]);

    const defaultValues = useMemo(() => {
        return normalizeSurveyDefaults(surveyResult, maxBudget, regionOptions);
    }, [surveyResult, maxBudget, regionOptions]);

    const isLoading = isPropertyLoading || isOfferLoading;

    return {
        defaultValues,
        surveyResult,
        hasPropertyContext: !!propertyDetail,
        hasOfferContext: !!offerDetail,
        displayedPrice,
        isLoading
    };
};
